import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Switch,
} from '@mui/material';
import { DataGrid, GridColDef, getGridStringOperators } from '@mui/x-data-grid';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { gamesService } from '../api/games';
import { commonGridStyles } from 'styles/gridStyles';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import SearchBar from 'components/common/SearchBar/SearchBar';
import styles from './games.module.scss';
import commonStyles from 'styles/common.module.scss';

interface EditableCellProps {
  id: number;
  initialValue: string | number | null;
  onSave: (id: number, newValue: string) => Promise<void>;
  type?: 'text' | 'number';
}

const EditableCell: React.FC<EditableCellProps> = ({ id, initialValue, onSave, type = 'text' }) => {
  const [value, setValue] = useState(
    initialValue === null || initialValue === undefined ? '' : String(initialValue),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setValue(initialValue === null || initialValue === undefined ? '' : String(initialValue));
  }, [initialValue]);

  const isModified =
    value !== (initialValue === null || initialValue === undefined ? '' : String(initialValue));

  const handleSave = async () => {
    if (!isModified || loading) return;
    setLoading(true);
    try {
      await onSave(id, value);
    } catch (error) {
      // Error is already handled by parent (toast displayed)
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <TextField
        size="small"
        value={value}
        onChange={(e) => {
          let val = e.target.value;
          if (type === 'number') {
            val = val.replace(/[^0-9.]/g, '');
          }
          setValue(val);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        disabled={loading}
        sx={{ flex: 1 }}
        inputProps={{
          sx: {
            color: 'var(--text-primary)',
            '&:disabled': {
              WebkitTextFillColor: 'var(--text-secondary)',
            },
          },
        }}
        InputProps={{
          sx: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border-color)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--text-secondary)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#00b3f5',
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border-color)',
            },
          },
        }}
      />
      <Button
        size="small"
        variant="contained"
        disabled={!isModified || loading}
        onClick={handleSave}
        sx={{
          backgroundColor: '#4caf50',
          color: '#fff',
          textTransform: 'none',
          minWidth: 'auto',
          padding: '2px 10px',
          '&:hover': {
            backgroundColor: '#43a047',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.3)',
          },
          flexShrink: 0,
        }}
      >
        Save
      </Button>
    </Box>
  );
};

const Providers: React.FC = () => {
  const dispatch = useAppDispatch();

  const [providers, setProviders] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string | number>('');
  const [subProviders, setSubProviders] = useState<any[]>([]);

  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingSubProviders, setLoadingSubProviders] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [selectedSubProviderForGames, setSelectedSubProviderForGames] = useState<any | null>(null);
  const [games, setGames] = useState<any[]>([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [gamesPage, setGamesPage] = useState(1);
  const [gamesPageSize, setGamesPageSize] = useState(10);
  const [gamesTotal, setGamesTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const response = await gamesService.getProviderList();
        if (response?.data) {
          setProviders(response.data);
          if (response.data.length > 0) {
            setSelectedProvider(response.data[0].id);
          }
        }
      } catch (error: any) {
        dispatch(
          showToast({
            text: error.response?.data?.message || 'Failed to fetch providers',
            type: 'error',
          }),
        );
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [dispatch]);

  const fetchSubProviders = useCallback(async () => {
    if (!selectedProvider) return;
    try {
      setLoadingSubProviders(true);
      const response = await gamesService.getSubProviderList({
        page,
        limit,
        sortField: 'id',
        sortValue: 'DESC',
        providerId: selectedProvider,
      });
      if (response?.data?.docs) {
        setSubProviders(response.data.docs);
        setTotal(response.data.totalDocs || 0);
      }
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to fetch sub-providers',
          type: 'error',
        }),
      );
    } finally {
      setLoadingSubProviders(false);
    }
  }, [page, limit, selectedProvider, dispatch]);

  useEffect(() => {
    fetchSubProviders();
  }, [fetchSubProviders]);

  const handleToggle = async (id: number, currentEnabled: boolean) => {
    try {
      const nextEnabled = !currentEnabled;
      await gamesService.toggleSubProvider(id, nextEnabled);
      dispatch(showToast({ text: 'Sub-provider status updated successfully', type: 'success' }));
      setSubProviders((prev) =>
        prev.map((sp) => (sp.id === id ? { ...sp, isEnabled: nextEnabled } : sp)),
      );
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to toggle status',
          type: 'error',
        }),
      );
    }
  };

  const handleUpdateField = async (id: number, field: string, value: any) => {
    if (value === '' && field !== 'commission' && field !== 'reply') return; // Allow empty commission or reply if needed

    try {
      const payload = { [field]: field === 'commission' ? (value ? Number(value) : null) : value };
      await gamesService.updateSubProvider(id, payload);
      dispatch(showToast({ text: `Sub-provider updated successfully`, type: 'success' }));
      setSubProviders((prev) => prev.map((sp) => (sp.id === id ? { ...sp, ...payload } : sp)));
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to update',
          type: 'error',
        }),
      );
      throw error;
    }
  };

  const fetchGames = useCallback(async () => {
    if (!selectedSubProviderForGames) return;
    try {
      setLoadingGames(true);
      const response = await gamesService.getGamesBySubProvider({
        subProviderId: selectedSubProviderForGames.id,
        page: gamesPage,
        pageSize: gamesPageSize,
        search: debouncedSearchQuery.trim() || undefined,
      });
      const items =
        response?.data?.items ||
        response?.data?.docs ||
        (Array.isArray(response?.data) ? response?.data : []);
      const totalCount =
        response?.data?.total ?? response?.data?.totalDocs ?? response?.total ?? items.length;

      setGames(items);
      setGamesTotal(totalCount);
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to fetch games',
          type: 'error',
        }),
      );
    } finally {
      setLoadingGames(false);
    }
  }, [selectedSubProviderForGames, gamesPage, gamesPageSize, debouncedSearchQuery, dispatch]);

  useEffect(() => {
    if (selectedSubProviderForGames) {
      fetchGames();
    }
  }, [fetchGames, selectedSubProviderForGames]);

  const handleDebouncedSearch = useCallback((val: string) => {
    setDebouncedSearchQuery((prev) => {
      const trimmed = val.trim();
      if (prev !== trimmed) {
        setGamesPage(1);
        return trimmed;
      }
      return prev;
    });
  }, []);

  const handleOpenGames = (subProviderRow: any) => {
    setSelectedSubProviderForGames(subProviderRow);
    setGames([]);
    setGamesPage(1);
    setGamesPageSize(10);
    setGamesTotal(0);
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  const handleBackFromGames = () => {
    setSelectedSubProviderForGames(null);
    setGames([]);
    setGamesPage(1);
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  const handleToggleGame = async (gameId: string | number, currentEnabled: boolean) => {
    const nextEnabled = !currentEnabled;
    // Optimistic update
    setGames((prev) => prev.map((g) => (g.id === gameId ? { ...g, isEnabled: nextEnabled } : g)));
    try {
      await gamesService.toggleGame(gameId, nextEnabled);
      dispatch(
        showToast({
          text: 'Game status updated successfully',
          type: 'success',
        }),
      );
    } catch (error: any) {
      // Revert optimistic update
      setGames((prev) =>
        prev.map((g) => (g.id === gameId ? { ...g, isEnabled: currentEnabled } : g)),
      );
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to update game status',
          type: 'error',
        }),
      );
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, overflow: 'hidden', mr: 1 }}>
            {params.row.image && (
              <Box
                component="img"
                src={params.row.image}
                alt={params.value}
                sx={{ width: 36, height: 26, borderRadius: 1, objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <Typography noWrap variant="body2" sx={{ fontWeight: 500 }}>
              {params.value}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenGames(params.row);
            }}
            sx={{
              ml: 'auto',
              backgroundColor: '#00b3f5',
              color: '#fff',
              textTransform: 'none',
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 8px',
              minWidth: 'auto',
              height: 24,
              lineHeight: 1,
              borderRadius: '4px',
              boxShadow: 'none',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#0092c7',
                boxShadow: 'none',
              },
            }}
          >
            Game Lists
          </Button>
        </Box>
      ),
    },
    {
      field: 'displayName',
      headerName: 'Display Name',
      width: 250,
      renderCell: (params) => (
        <EditableCell
          id={params.row.id}
          initialValue={params.value}
          onSave={(id, val) => handleUpdateField(id, 'displayName', val)}
        />
      ),
    },
    {
      field: 'commission',
      headerName: 'Commission (%)',
      width: 200,
      renderCell: (params) => (
        <EditableCell
          id={params.row.id}
          initialValue={params.value}
          type="number"
          onSave={(id, val) => handleUpdateField(id, 'commission', val)}
        />
      ),
    },
    {
      field: 'gameReplyUrl',
      headerName: 'Game Reply URL',
      width: 300,
      renderCell: (params) => (
        <EditableCell
          id={params.row.id}
          initialValue={params.value}
          onSave={(id, val) => handleUpdateField(id, 'gameReplyUrl', val)}
        />
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Switch
          size="small"
          checked={params.value}
          onChange={() => handleToggle(params.row.id, params.value)}
          color="primary"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#00b3f5' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#00b3f5',
            },
          }}
        />
      ),
    },
  ];

  const gameColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'imageUrl',
      headerName: 'Image',
      width: 90,
      sortable: false,
      renderCell: (params) => {
        const img = params.value || params.row.image;
        return img ? (
          <Box
            component="img"
            src={img}
            alt={params.row.title || 'Game'}
            sx={{
              width: 44,
              height: 34,
              borderRadius: 1,
              objectFit: 'cover',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            }}
          />
        ) : (
          <Box
            sx={{
              width: 44,
              height: 34,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              color: 'var(--text-secondary)',
            }}
          >
            -
          </Box>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Game Title',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 500, color: 'var(--text-primary)' }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Switch
          size="small"
          checked={Boolean(params.value)}
          onChange={() => handleToggleGame(params.row.id, Boolean(params.value))}
          color="primary"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#00b3f5' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#00b3f5',
            },
          }}
        />
      ),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={page - 1}
      onPageChange={(_, newPage) => setPage(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
      }}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  const renderGamesPagination = () => (
    <CustomPagination
      count={gamesTotal || 0}
      page={Math.max(0, gamesPage - 1)}
      onPageChange={(_, newPage) => setGamesPage(newPage + 1)}
      rowsPerPage={gamesPageSize}
      onRowsPerPageChange={(e) => {
        setGamesPageSize(parseInt(e.target.value, 10));
        setGamesPage(1);
      }}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  if (selectedSubProviderForGames) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={handleBackFromGames}
              sx={{
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                px: 2,
                py: 0.75,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: '#00b3f5',
                  bgcolor: 'rgba(0, 179, 245, 0.08)',
                },
              }}
            >
              Go Back
            </Button>
            <Box>
              <Typography variant="h5" className={styles.title} sx={{ m: 0 }}>
                Game Lists:{' '}
                {selectedSubProviderForGames.displayName || selectedSubProviderForGames.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
                Sub-Provider ID: {selectedSubProviderForGames.id} | Total Games: {gamesTotal}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 260, maxWidth: 360, width: { xs: '100%', sm: 'auto' } }}>
            <SearchBar
              placeholder="Search game..."
              value={searchQuery}
              onChange={setSearchQuery}
              onDebounceChange={handleDebouncedSearch}
              debounceMs={300}
            />
          </Box>
        </Box>

        <Box className={commonStyles.gridWrapper}>
          {renderGamesPagination()}
          <DataGrid
            rows={games}
            columns={gameColumns}
            loading={loadingGames}
            getRowId={(row) => row.id ?? row.gameId}
            autoHeight
            sx={{
              ...commonGridStyles,
            }}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={gamesTotal || 0}
            paginationModel={{
              page: Math.max(0, gamesPage - 1),
              pageSize: gamesPageSize,
            }}
            onPaginationModelChange={(model) => {
              setGamesPage(model.page + 1);
              setGamesPageSize(model.pageSize);
            }}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 4,
                    color: 'var(--text-secondary)',
                  }}
                >
                  No games found
                </Box>
              ),
            }}
          />
          {renderGamesPagination()}
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Providers
        </Typography>

        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Provider</InputLabel>
              <Select
                value={selectedProvider}
                label="Provider"
                onChange={(e) => {
                  setSelectedProvider(e.target.value as string | number);
                  setPage(1);
                }}
                className={styles.select}
                disabled={loadingProviders}
              >
                {providers?.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={subProviders}
          columns={columns}
          loading={loadingSubProviders}
          getRowId={(row) => row.id}
          autoHeight
          sx={{
            ...commonGridStyles,
          }}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={total}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 4,
                  color: 'var(--text-secondary)',
                }}
              >
                No sub-providers found
              </Box>
            ),
          }}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default Providers;
